/**
 * Discoveries API
 * Manages discovery review queue, standard transitions, and conflict resolution
 */

import { apiFetch } from './config';

/** A discovered or proposed standard awaiting review */
export interface Discovery {
  id: string;
  project_id: string;
  name: string;
  description: string;
  confidence: number;
  source: 'llm' | 'github' | 'upload';
  category: string;
  status: 'proposed' | 'approved' | 'rejected' | 'observing';
  rule_text?: string;
  action_type?: string;
  evidence?: string;
  created_at: string;
  updated_at?: string;
}

/** Payload for transitioning a standard's status */
export interface TransitionPayload {
  action: 'approve' | 'reject' | 'observe';
  reason?: string;
  reason_code?: string;
  feedback?: string;
}

/** A conflict between an uploaded standard and a discovered pattern */
export interface StandardConflict {
  conflict_id: string;
  project_id: string;
  uploaded: ConflictSide;
  discovered: ConflictSide;
  status: 'pending' | 'resolved';
  created_at: string;
}

/** One side of a standard conflict */
export interface ConflictSide {
  id: string;
  name: string;
  rule_text: string;
  action_type: string;
  source: string;
  evidence?: string;
}

/** Resolution payload for a conflict */
export interface ConflictResolution {
  resolution: 'keep_uploaded' | 'use_discovered' | 'merge' | 'dismiss';
  mergedRule?: string;
}

export const discoveriesApi = {
  /**
   * Get discoveries for a project, optionally filtered by status
   */
  getDiscoveries: async (projectId: string, status?: string) => {
    const params = new URLSearchParams();
    params.set('project_id', projectId);
    if (status) params.set('status', status);
    return apiFetch<{ records: Discovery[]; total: number }>(
      `/api/standards/discoveries?${params.toString()}`
    );
  },

  /**
   * Transition a standard (approve, reject, or keep observing)
   */
  transitionStandard: async (standardId: string, payload: TransitionPayload) => {
    return apiFetch<{ success: boolean; message: string }>(
      '/api/standards/transition',
      {
        method: 'POST',
        body: JSON.stringify({ standard_id: standardId, ...payload }),
      }
    );
  },

  /**
   * Resolve a conflict between uploaded and discovered standards
   */
  resolveConflict: async (conflictId: string, resolution: ConflictResolution) => {
    return apiFetch<{ success: boolean; message: string }>(
      '/api/standards/conflicts/resolve',
      {
        method: 'POST',
        body: JSON.stringify({ conflict_id: conflictId, ...resolution }),
      }
    );
  },
};
